import { ImmomioButtonComponent } from '@immomio/ui/button';
import {
  Component,
  inject,
  OnInit,
  input,
  ChangeDetectionStrategy
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { of } from 'rxjs';
import { take } from 'rxjs/operators';

import { Attachment } from '@ui/shared/model';

import { TranslatePipe } from '@ngx-translate/core';

import { SvgIconComponent } from 'angular-svg-icon';
import {
  AttachmentComponentHelperCtor,
  mixinAttachmentHelper
} from '../attachment-component-helper.mixin';
import { FileSizePipe } from '../../../../pipes/file-size.pipe';
import { ModalFooterComponent } from '../../modal/modal-footer/modal-footer.component';
import { LoadingSpinnerComponent } from '../../loading-spinner/loading-spinner.component';
import { SimpleTextInputComponent } from '../../form/controls/simple-text-input/simple-text-input.component';
import { ModalContentComponent } from '../../modal/modal-content/modal-content.component';
import { ModalComponent } from '../../modal/modal.component';

class EditAttachmentComponentBase {}

const _EditAttachmentComponentMixinBase: AttachmentComponentHelperCtor =
  mixinAttachmentHelper(EditAttachmentComponentBase);

const ROTATE_ANGLE = 90;

@Component({
  selector: 'app-edit-attachment',
  templateUrl: './edit-attachment.component.html',
  styleUrls: ['./edit-attachment.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ImmomioButtonComponent,
    ModalComponent,
    FormsModule,
    ReactiveFormsModule,
    ModalContentComponent,
    SimpleTextInputComponent,
    SvgIconComponent,
    LoadingSpinnerComponent,
    ModalFooterComponent,
    TranslatePipe,
    FileSizePipe
  ]
})
export class EditAttachmentComponent
  extends _EditAttachmentComponentMixinBase
  implements OnInit
{
  private sanitizer = inject(DomSanitizer);
  private ngbActiveModal = inject(NgbActiveModal);
  private http = inject(HttpClient);

  public readonly attachment = input<Attachment>(undefined);
  public readonly removeFn = input<() => void>(() => {
    // Placeholder
  });

  public imgSrc: SafeUrl | string;
  public loading = false;

  public form = new FormGroup({
    title: new FormControl('')
  });

  public ngOnInit() {
    const attachment = this.attachment();
    attachment.rotate = attachment.rotate || 0;
    this.imgSrc = this.hasFile
      ? this.getSafeUrl(attachment.file)
      : attachment.url;

    this.form.patchValue({
      title: this.name
    });

    if (!this.hasFile) {
      this.loading = true;
      this.getBlob(attachment).subscribe(
        blob => {
          if (blob) {
            this.attachment().file = new File(
              [blob],
              `${this.attachment().title}.${this.attachment().extension}`
            );
          }

          this.loading = false;
        },
        err => {
          console.error(err);
          this.loading = false;
        }
      );
    }
  }

  public rotate() {
    const attachment = this.attachment();
    attachment.rotate = attachment.rotate + ROTATE_ANGLE;

    if (attachment.rotate === 360) {
      attachment.rotate = 0;
    }
  }

  public remove() {
    this.removeFn()();
    this.cancel();
  }

  public cancel() {
    this.ngbActiveModal.dismiss();
  }

  public save() {
    this.ngbActiveModal.close({
      attachment: this.attachment(),
      formValue: this.form.value
    });
  }

  public getSafeUrl(file: Blob) {
    return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
  }

  public getBlob(attachment: Attachment) {
    if (!attachment || !attachment.url) {
      return of(null);
    }

    /**
     * Forces browser to request given image once again,
     * instead of getting it from it's cache.
     */
    const refreshParam = new Date().getTime();
    const url = attachment.url;

    return this.http
      .get(`${url}?${refreshParam}`, {
        responseType: 'blob'
      })
      .pipe(take(1));
  }
}
